class CartDrawer extends HTMLElement {
  static isDrawerEnabled() {
    return (
      document.body.dataset.cartType === 'drawer' ||
      window.theme?.cartType === 'drawer' ||
      Boolean(document.querySelector('cart-drawer'))
    );
  }

  static parseCartChangeError(error) {
    if (window.CartItems?.parseCartChangeError) {
      return window.CartItems.parseCartChangeError(error);
    }

    const raw = error?.message || '';
    const jsonMatch = raw.match(/\{[\s\S]*\}$/);
    if (!jsonMatch) return null;

    try {
      const data = JSON.parse(jsonMatch[0]);
      return data.description || data.message || null;
    } catch {
      return null;
    }
  }

  static showCartError(message) {
    if (window.CartItems?.showCartError) {
      window.CartItems.showCartError(message);
      return;
    }

    const errorsEl = document.getElementById('cart-errors');
    if (errorsEl && message) errorsEl.textContent = message;
  }

  static syncCartAfterChangeError(cartItemsEl) {
    if (window.CartItems?.syncCartAfterChangeError) {
      return window.CartItems.syncCartAfterChangeError(cartItemsEl);
    }

    const cartUrl = window.routes?.cart_url?.endsWith?.('.js')
      ? window.routes.cart_url
      : `${window.routes?.cart_url || '/cart'}.js`;

    return fetch(cartUrl, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        cartItemsEl?.refreshCartUI?.(cart, cart);
        return cart;
      });
  }

  constructor() {
    super();

    this.handleDocumentCartClick = this.handleDocumentCartClick.bind(this);
    this.handleDrawerRemoveClick = this.handleDrawerRemoveClick.bind(this);
    this.handleDrawerQuantityButtonClick = this.handleDrawerQuantityButtonClick.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleDrawerRemoveClick, true);
    this.addEventListener('click', this.handleDrawerQuantityButtonClick, true);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.querySelectorAll('[data-cart-drawer-close]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  connectedCallback() {
    if (!CartDrawer.removeClickBound) {
      CartDrawer.removeClickBound = true;
      document.addEventListener('click', CartDrawer.handleDocumentRemoveClick, true);
    }
    document.addEventListener('click', this.handleDocumentCartClick, true);

    if (!this.dataset.drawerChangeBound) {
      this.dataset.drawerChangeBound = 'true';
      this.addEventListener('change', this.handleDrawerQuantityChange.bind(this));
    }
  }

  applyCrossSellConfigFromSection(sectionHTML) {
    if (!sectionHTML) return null;

    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const source = doc.querySelector('#cart-drawer-cross-sell');
    const target = this.querySelector('#cart-drawer-cross-sell');

    if (!source?.dataset?.xsellConfig || !target) return null;

    target.dataset.xsellConfig = source.dataset.xsellConfig;
    return window.xsellCustom?.parseCrossSellConfig?.(source.dataset.xsellConfig) || null;
  }

  handleDrawerQuantityButtonClick(event) {
    const button = event.target.closest('.quantity__button');
    if (!button || !this.contains(button)) return;

    const input = button.closest('quantity-input')?.querySelector('[name="updates[]"]');
    if (!input?.dataset?.index) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const previousValue = input.value;
    if (button.name === 'plus') {
      input.stepUp();
    } else {
      input.stepDown();
    }

    if (input.value === previousValue) return;

    input.dataset.qtyUpdating = 'true';
    this.updateLineQuantity({
      line: input.dataset.index,
      key: input.dataset.key,
      quantity: input.value,
    }).finally(() => {
      delete input.dataset.qtyUpdating;
    });
  }

  handleDrawerQuantityChange(event) {
    if (event.cartChangeHandled) return;

    const input = event.target.closest('[name="updates[]"]');
    if (!input || !this.contains(input)) return;
    if (input.dataset.qtyUpdating === 'true') return;

    event.cartChangeHandled = true;

    this.updateLineQuantity({
      line: input.dataset.index,
      key: input.dataset.key,
      quantity: input.value,
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentCartClick, true);
  }

  static getRemoveControl(event) {
    return (
      event.target.closest('.cart-item__remove-link') ||
      event.target.closest('cart-remove-button') ||
      event.target.closest('a[href*="/cart/change"]')
    );
  }

  static getLineDataFromControl(removeControl) {
    if (removeControl.matches('a[href*="/cart/change"]')) {
      try {
        const url = new URL(removeControl.href, window.location.origin);
        return {
          line: url.searchParams.get('line'),
          key: url.searchParams.get('id'),
        };
      } catch {
        return { line: null, key: null };
      }
    }

    const wrapper = removeControl.closest('cart-remove-button');
    return {
      line: removeControl.dataset?.index || wrapper?.dataset?.index,
      key: removeControl.dataset?.key || wrapper?.dataset?.key,
    };
  }

  static handleDocumentRemoveClick(event) {
    if (!CartDrawer.isDrawerEnabled() || event.cartRemoveHandled) return;

    const removeControl = CartDrawer.getRemoveControl(event);
    if (!removeControl) return;

    const drawer = removeControl.closest('cart-drawer');
    if (!drawer) return;

    CartDrawer.removeLineFromDrawer(drawer, removeControl, event);
  }

  handleDrawerRemoveClick(event) {
    const removeControl = CartDrawer.getRemoveControl(event);
    if (!removeControl || !this.contains(removeControl)) return;

    CartDrawer.removeLineFromDrawer(this, removeControl, event);
  }

  static removeLineFromDrawer(drawer, removeControl, event) {
    if (event?.cartRemoveHandled) return;
    if (event) {
      event.cartRemoveHandled = true;
      event.preventDefault();
      event.stopPropagation();
    }

    const { line, key } = CartDrawer.getLineDataFromControl(removeControl);
    if (!line && !key) return;

    const drawerInstance =
      drawer instanceof CartDrawer ? drawer : document.getElementById('cart-drawer');

    drawerInstance?.removeCartLine?.({
      line,
      key,
      row: removeControl.closest('.cart-item'),
    });
  }

  getCartChangeUrl() {
    const base = window.routes?.cart_change_url || '/cart/change';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  getRowKey(row) {
    return (
      row?.querySelector('[name="updates[]"]')?.dataset?.key ||
      row?.querySelector('.cart-item__remove-link')?.dataset?.key ||
      row?.querySelector('cart-remove-button')?.dataset?.key
    );
  }

  getRowLine(row) {
    return (
      row?.querySelector('[name="updates[]"]')?.dataset?.index ||
      row?.querySelector('.cart-item__remove-link')?.dataset?.index ||
      row?.querySelector('cart-remove-button')?.dataset?.index
    );
  }

  removeLineFromDOM({ line, key, row }) {
    document.querySelectorAll('#CartDrawer-CartItems .cart-item').forEach((itemRow) => {
      const rowKey = this.getRowKey(itemRow);
      const rowLine = this.getRowLine(itemRow);
      const isClickedRow = row && itemRow === row;
      const matchesKey = key && rowKey === key;
      const matchesLine = line != null && line !== '' && String(rowLine) === String(line);

      if (isClickedRow || matchesKey || matchesLine) {
        itemRow.remove();
      }
    });

    if (line) {
      document.getElementById(`CartItem-${line}`)?.remove();
    }

    const itemsTarget = document.querySelector('#CartDrawer-CartItems .js-contents');
    if (itemsTarget && !itemsTarget.querySelector('.cart-item')) {
      itemsTarget.innerHTML = '';
    }
  }

  pruneDrawerItemsFromCart(cart) {
    const activeKeys = new Set(
      (cart.items || [])
        .filter((item) => (item.quantity || 0) > 0)
        .map((item) => item.key)
        .filter(Boolean)
    );

    document.querySelectorAll('#CartDrawer-CartItems .cart-item').forEach((itemRow) => {
      const rowKey = this.getRowKey(itemRow);
      if (!rowKey || !activeKeys.has(rowKey)) {
        itemRow.remove();
      }
    });

    if (cart.item_count === 0 || activeKeys.size === 0) {
      const itemsTarget = document.querySelector('#CartDrawer-CartItems .js-contents');
      const footerTarget = document.querySelector('#CartDrawer-Footer .js-contents');
      if (itemsTarget) itemsTarget.innerHTML = '';
      if (footerTarget) footerTarget.innerHTML = '';
    }
  }

  syncDrawerTotalsAfterChange(cart, cartItems) {
    cartItems?.refreshCartUI?.(cart, cart);
    this.updateMonetaryValues(cart);
    this.setEmptyState(cart.item_count ?? 0);

    return fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((freshCart) => {
        this.pruneDrawerItemsFromCart(freshCart);
        cartItems?.refreshCartUI?.(freshCart, freshCart);
        this.updateMonetaryValues(freshCart);
        this.setEmptyState(freshCart.item_count ?? 0);
        this.refreshCrossSell();
        return freshCart;
      });
  }

  removeCartLine({ line, key, row }) {
    const removeId = key || `line-${line}`;
    if (this._removeInFlight === removeId) {
      return Promise.resolve();
    }
    this._removeInFlight = removeId;

    const cartItems = this.querySelector('cart-items');
    this.removeLineFromDOM({ line, key, row });
    if (line) cartItems?.enableLoading?.(line);

    const payload = { quantity: 0 };
    if (key) {
      payload.id = String(key);
    } else if (line != null && line !== '') {
      payload.line = parseInt(line, 10);
    }

    return fetch(this.getCartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`Cart change failed: ${response.status} ${text}`);
          });
        }
        return response.json();
      })
      .then((cart) => {
        this.pruneDrawerItemsFromCart(cart);
        return this.syncDrawerTotalsAfterChange(cart, cartItems);
      })
      .catch((error) => {
        const shopifyMessage = CartDrawer.parseCartChangeError(error);
        if (!shopifyMessage) console.error('Cart drawer: remove failed', error);
        CartDrawer.showCartError(shopifyMessage || window.cartStrings?.error);

        return fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => this.syncDrawerTotalsAfterChange(cart, cartItems));
      })
      .finally(() => {
        delete this._removeInFlight;
        cartItems?.disableLoading?.();
      });
  }

  buildCartChangePayload({ line, key, quantity }) {
    const qty = parseInt(quantity, 10);
    const isRemove = qty === 0;

    const payload = {
      quantity: Number.isNaN(qty) ? 0 : qty,
    };

    if (key) {
      payload.id = String(key);
    } else if (line != null && line !== '') {
      payload.line = parseInt(line, 10);
    }

    if (isRemove) {
      const sectionId = this.getSectionId();
      payload.sections = [sectionId, 'cart-icon-bubble'];
      payload.sections_url = window.location.pathname + window.location.search;
    }

    return payload;
  }

  fetchDrawerSections(cartState) {
    const sectionIds = this.getSectionIdsToRequest().join(',');
    const sectionsUrl = window.location.pathname + window.location.search;
    const url = `${window.routes.root_url}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(sectionsUrl)}&_=${Date.now()}`;

    return fetch(url, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((sections) => ({ ...cartState, sections: sections.sections || sections }));
  }

  updateLineQuantity({ line, key, quantity }) {
    const cartItems = this.querySelector('cart-items');

    if (parseInt(quantity, 10) === 0) {
      return this.removeCartLine({ line, key });
    }

    if (line) cartItems?.enableLoading?.(line);

    const payload = this.buildCartChangePayload({ line, key, quantity });

    if (!payload.id && payload.line == null) {
      console.error('Cart drawer: missing line item identifier');
      return Promise.resolve();
    }

    const fetchHeaders =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          };

    return fetch(this.getCartChangeUrl(), {
      ...fetchHeaders,
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`Cart change failed: ${response.status} ${text}`);
          });
        }
        return response.json();
      })
      .then((cartState) => {
        if (cartItems?.refreshCartUI) {
          cartItems.refreshCartUI(cartState, cartState);
        } else {
          this.updateMonetaryValues(cartState);
        }

        this.setEmptyState(cartState.item_count ?? 0);

        if (cartItems?.syncDrawerFromServer) {
          return cartItems.syncDrawerFromServer(cartState, { itemsOnly: true });
        }

        return cartState;
      })
      .then((cartState) => {
        if (cartItems?.refreshCartUI) {
          cartItems.refreshCartUI(cartState, cartState);
        } else {
          this.updateMonetaryValues(cartState);
        }

        this.setEmptyState(cartState.item_count ?? 0);
        this.classList.add('active');
        document.body.classList.add('overflow-hidden');
        return cartState;
      })
      .catch((error) => {
        const shopifyMessage = CartDrawer.parseCartChangeError(error);
        if (!shopifyMessage) console.error('Cart drawer: line update failed', error);
        CartDrawer.showCartError(shopifyMessage || window.cartStrings?.error);
        return CartDrawer.syncCartAfterChangeError(cartItems);
      })
      .finally(() => {
        cartItems?.disableLoading?.();
      });
  }

  handleDocumentCartClick(event) {
    if (!CartDrawer.isDrawerEnabled()) return;

    if (
      event.target.closest(
        'cart-remove-button, .cart-item__remove-link, a[href*="/cart/change"]'
      )
    ) {
      return;
    }

    const cartPageLink = event.target.closest('a[href]');
    if (cartPageLink && this.isCartPageLink(cartPageLink)) {
      if (cartPageLink.classList.contains('cart-drawer__view-cart')) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(cartPageLink);
      return;
    }

    const trigger = event.target.closest('[data-cart-drawer-trigger], #cart-icon-bubble');
    if (!trigger) return;

    if (trigger.classList.contains('cart-drawer__view-cart')) return;

    event.preventDefault();
    event.stopPropagation();
    this.open(trigger);
  }

  isCartPageLink(link) {
    if (!link.href) return false;

    try {
      const url = new URL(link.href, window.location.origin);
      const cartPath = new URL(window.routes.cart_url, window.location.origin).pathname;
      return url.pathname === cartPath;
    } catch {
      return false;
    }
  }

  bindTriggers() {
    /* Delegated click handler on document covers cart icon after AJAX updates */
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');

    if (!this.classList.contains('is-empty')) {
      this.refreshCrossSell();
    }

    const panel = this.querySelector('#CartDrawer');
    trapFocus(panel, panel.querySelector('.cart-drawer__close'));
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionId() {
    return (
      document.getElementById('CartDrawer-CartItems')?.dataset?.id ||
      this.dataset.sectionId ||
      'cart-drawer'
    );
  }

  getSectionsToRender() {
    const sectionId = this.getSectionId();

    return [
      {
        id: 'CartDrawer-CartItems',
        section: sectionId,
        containerSelector: '#CartDrawer-CartItems',
        selector: '.js-contents',
      },
      {
        id: 'CartDrawer-Footer',
        section: sectionId,
        containerSelector: '#CartDrawer-Footer',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        containerSelector: '#cart-icon-bubble',
        selector: '.cart-icon-bubble__inner',
      },
    ];
  }

  getSectionIdsToRequest() {
    return [...new Set(this.getSectionsToRender().map((config) => config.section))];
  }

  getItemCount(parsedState) {
    if (typeof parsedState.item_count === 'number') {
      return parsedState.item_count;
    }

    if (Array.isArray(parsedState.items)) {
      return parsedState.items.reduce((total, item) => total + item.quantity, 0);
    }

    if (parsedState.quantity) {
      return parsedState.quantity;
    }

    return null;
  }

  setEmptyState(itemCount) {
    const isEmpty = itemCount === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);

    const crossSell = this.querySelector('#cart-drawer-cross-sell');
    if (crossSell) {
      if (isEmpty) {
        crossSell.innerHTML = '';
        crossSell.hidden = true;
      }
    }
  }

  refreshCrossSell() {
    clearTimeout(this._crossSellRefreshTimer);

    this._crossSellRefreshTimer = setTimeout(() => {
      this._runRefreshCrossSell();
    }, 120);

    return Promise.resolve();
  }

  _runRefreshCrossSell() {
    if (this.classList.contains('is-empty')) {
      const crossSell = this.querySelector('#cart-drawer-cross-sell');
      if (crossSell) {
        crossSell.innerHTML = '';
        crossSell.hidden = true;
      }
      return Promise.resolve();
    }

    const sectionId = this.getSectionId();
    const url = `${window.routes.root_url}?sections=${encodeURIComponent(sectionId)}&sections_url=${encodeURIComponent(window.location.pathname)}`;

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const sectionHTML = this.resolveSectionHtml(data, sectionId);
        if (!sectionHTML) return;

        const config = this.applyCrossSellConfigFromSection(sectionHTML);
        if (config) {
          return window.xsellCustom?.renderDrawerCrossSell?.(config);
        }
      })
      .catch((error) => console.error('Cart drawer: cross-sell refresh failed', error));
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    if (!parsedState.sections) {
      console.error('Cart drawer: missing sections in cart add response', parsedState);
      this.refreshSections().then(() => this.open());
      return;
    }

    const sectionId = this.getSectionId();
    const drawerSectionHTML = this.resolveSectionHtml(parsedState.sections, sectionId);

    this.getSectionsToRender().forEach((config) => {
      if (config.section === 'cart-icon-bubble') {
        const bubbleHTML = parsedState.sections?.['cart-icon-bubble'];
        if (!bubbleHTML) return;

        const content = this.getSectionInnerHTML(bubbleHTML, config);
        if (!content) return;

        const container = document.querySelector(config.containerSelector);
        const target = container?.querySelector(config.selector) || container;
        if (target) target.innerHTML = content;
        return;
      }

      if (!drawerSectionHTML) return;

      const content = this.getSectionInnerHTML(drawerSectionHTML, config);
      if (!content) return;

      const container = document.querySelector(config.containerSelector);
      const target = container?.querySelector(config.selector) || container;
      if (target) {
        target.innerHTML = content;
        this.upgradeCartItemComponents(target);
      }
    });

    const itemCount = this.getItemCount(parsedState);
    if (itemCount !== null) {
      this.setEmptyState(itemCount);
    } else {
      this.classList.remove('is-empty');
      this.querySelector('cart-items')?.classList.remove('is-empty');
    }

    fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        const cartItems = this.querySelector('cart-items');
        if (cartItems?.refreshCartUI) {
          cartItems.refreshCartUI(cart, parsedState);
        } else {
          this.updateMonetaryValues(cart);
        }
        this.setEmptyState(cart.item_count);
        this.refreshCrossSell();
      })
      .catch(() => {
        const cartItems = this.querySelector('cart-items');
        if (cartItems?.refreshCartUI) {
          cartItems.refreshCartUI(parsedState, parsedState);
        } else {
          this.updateMonetaryValues(parsedState);
        }
        this.refreshCrossSell();
      });

    this.bindTriggers();
    document.querySelector('sticky-header')?.reveal?.();
    this.open();
  }

  getSectionInnerHTML(sectionHTML, config) {
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const root = doc.querySelector('.shopify-section') || doc;

    if (config.containerSelector) {
      const container = root.querySelector(config.containerSelector);
      const scopedTarget = container?.querySelector(config.selector);
      if (scopedTarget) return scopedTarget.innerHTML;
    }

    const target = root.querySelector(config.selector);
    return target ? target.innerHTML : '';
  }

  resolveSectionHtml(sections, sectionId) {
    if (!sections) return null;
    if (sections[sectionId]) return sections[sectionId];
    if (sections['cart-drawer']) return sections['cart-drawer'];

    const matchingKey = Object.keys(sections).find(
      (key) => key === sectionId || key.endsWith('__cart-drawer') || key.includes('cart-drawer')
    );

    return matchingKey ? sections[matchingKey] : null;
  }

  upgradeCartItemComponents(root) {
    root.querySelectorAll('cart-remove-button, quantity-input').forEach((element) => {
      customElements.upgrade(element);
    });
  }

  updateMonetaryValues(cart) {
    const totalCents = cart?.total_price ?? cart?.items_subtotal_price;
    if (totalCents == null || totalCents === '') return;

    const format = window.theme?.moneyFormat || Shopify.money_format;
    const formattedTotal = Shopify.formatMoney(totalCents, format);

    document.querySelectorAll('#CartDrawer-Footer .totals__subtotal-value').forEach((el) => {
      el.innerHTML = formattedTotal;
    });

    this.updateShippingBar(totalCents);
  }

  updateShippingBar(totalCents) {
    if (typeof window.updateFreeShippingBar === 'function') {
      window.updateFreeShippingBar(totalCents);
    }
  }

  refreshSections() {
    const sectionId = this.getSectionId();
    const url = `${window.routes.root_url}?sections=${encodeURIComponent(sectionId)},cart-drawer&sections_url=${encodeURIComponent(window.location.pathname)}`;

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const sectionHTML = this.resolveSectionHtml(data.sections, sectionId);
        if (!sectionHTML) return;

        this.getSectionsToRender()
          .filter((config) => config.section !== 'cart-icon-bubble')
          .forEach((config) => {
            const content = this.getSectionInnerHTML(sectionHTML, config);
            if (!content) return;

            const container = document.querySelector(config.containerSelector);
            const target = container?.querySelector(config.selector) || container;
            if (target) {
              target.innerHTML = content;
              this.upgradeCartItemComponents(target);
            }
          });

        return fetch(window.routes.cart_url + '.js')
          .then((r) => r.json())
          .then((cart) => {
            this.setEmptyState(cart.item_count);
            this.updateMonetaryValues(cart);
            this.refreshCrossSell();
          });
      })
      .catch((error) => console.error('Cart drawer: failed to refresh sections', error));
  }
}

customElements.define('cart-drawer', CartDrawer);

// Fix cross-sell placeholder images. The xsell CDN script (custom_drawer.js) calls
// buildCrossSellCardImage which returns a placeholder div when theme_settings is empty.
// We watch the cross-sell container and replace placeholders with real images from
// the imgsrc stored in the xsell config data attribute.
(function fixCrossSellImages() {
  function getImgsrcMap() {
    const el = document.querySelector('#cart-drawer-cross-sell');
    if (!el || !el.dataset.xsellConfig) return {};
    try {
      const config = JSON.parse(el.dataset.xsellConfig);
      const map = {};
      (config.data || []).forEach(function(p) {
        if (p.handle && p.imgsrc) map[p.handle] = { src: p.imgsrc, title: p.title || '' };
      });
      return map;
    } catch (e) {
      return {};
    }
  }

  function fillPlaceholders() {
    const grid = document.querySelector('.cart-drawer__crosssell-grid');
    if (!grid) return;
    const imgMap = getImgsrcMap();
    grid.querySelectorAll('.cart-drawer__crosssell-media-placeholder').forEach(function(placeholder) {
      const mediaDiv = placeholder.closest('.cart-drawer__crosssell-media');
      const cardWrapper = mediaDiv && mediaDiv.closest('[data-xsorder]');
      if (!mediaDiv || !cardWrapper) return;
      // Find which product this card is for via the product URL or title
      const link = cardWrapper.querySelector('a.full-unstyled-link');
      const handle = link && link.href !== 'undefined'
        ? link.href.split('/products/')[1]?.split('?')[0]
        : null;
      // Fall back: match by order attribute
      const order = parseInt(cardWrapper.dataset.xsorder, 10);
      const imgData = (handle && imgMap[handle])
        || Object.values(imgMap)[order - 1]
        || null;
      if (!imgData) return;
      const img = document.createElement('img');
      img.src = imgData.src;
      img.alt = imgData.title;
      img.loading = 'lazy';
      img.className = 'motion-reduce';
      img.style.cssText = 'object-fit:cover;width:100%;height:100%;';
      placeholder.replaceWith(img);
    });
  }

  const observer = new MutationObserver(function() { fillPlaceholders(); });
  document.addEventListener('DOMContentLoaded', function() {
    const crossSell = document.querySelector('#cart-drawer-cross-sell');
    if (crossSell) observer.observe(crossSell, { childList: true, subtree: true });
  });
})();
